import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import ExpandableCard from '../../../commons/ExpandableCard';
import { LinearGaugeWithSubtitle } from '../../../commons/LinearGaugeWithSubtitle';
import UserBadgeThumbnails from '../../Dashboard/DashboardComponents/UserBadgeThumbnails';
import { OMOutlierSummaryStats } from '../types/types';
import { StateContext } from '../../../GlobalStateStore';

interface ExceptionMetrics {
  totalExceptions: number;
  pendingExceptions: number;
  acceptedExceptions: number;
  deniedExceptions: number;
}

interface ExceptionOverviewCardProps {
  metrics: ExceptionMetrics;
  tmStats: OMOutlierSummaryStats[];
}

export const ExceptionOverviewCard: React.FC<ExceptionOverviewCardProps> = ({
  metrics,
  tmStats,
}) => {
  const reviewedExceptions =
    metrics.acceptedExceptions + metrics.deniedExceptions;
  const completionPercentage =
    metrics.totalExceptions === 0
      ? 0
      : Math.round((reviewedExceptions / metrics.totalExceptions) * 100);

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;

  const header = (
    <Box spacingInset='400 300 300 300'>
      <Row alignmentHorizontal='justify' alignmentVertical='center'>
        <Column spacing='small'>
          <Text type='h400'>{completionPercentage}%</Text>
          <Text type='h50'>
            <FormattedMessage
              id='cs_perfportal_flag_reviews_completed'
              defaultMessage='Flag reviews completed'
            />
          </Text>
        </Column>
        <Box width='180'>
          <LinearGaugeWithSubtitle
            total={metrics.totalExceptions}
            value={reviewedExceptions}
            width={180}
            topSubtitle='Flagged contacts'
            subtitle={
              <FormattedMessage
                id='cs_perfportal_exception_review_count'
                defaultMessage='{reviewed}/{total} reviewed'
                values={{
                  reviewed: reviewedExceptions,
                  total: metrics.totalExceptions,
                }}
              />
            }
          />
        </Box>
      </Row>
    </Box>
  );

  return (
    <ExpandableCard
      title={
        <FormattedMessage
          id='cs_perfportal_invalid_contact_flags'
          defaultMessage='Invalid contact flags'
        />
      }
      header={header}
      expandedContent={<OMInvalidContactFlagsExpandedCard tmStats={tmStats} />}
      cardSpacingInset='350 450 450 450'
      titleRowSpacing='300 300 200 400'
      clickstreamTags={{
        'data-csa-c-type': 'link',
        'data-csa-c-slot-id': `om-outlier-management--invalid-contact-link-slot`,
        'data-csa-c-tag1': 'Coaching',
        'data-csa-c-tag2': 'Invalid Contacts',
        'data-csa-c-profile-id': requesterUserID,
        'data-csa-c-username': requesterUsername,
        'data-csa-c-is-TM': isTm,
        'data-csa-c-is-OM': isOm,
      }}
    />
  );
};

interface OMInvalidContactFlagsTMInfo {
  name: string;
  login: string;
  numAssociates: number;
  numInvalidFlags: number;
}

interface OMInvalidContactFlagsExpandedCardProps {
  tmStats: OMOutlierSummaryStats[];
}

const OMInvalidContactFlagsExpandedCard: React.FC<
  OMInvalidContactFlagsExpandedCardProps
> = ({ tmStats }) => {
  const expandedContent = (
    <Column spacing='small' spacingInset='none 200 none none'>
      <Row
        alignmentHorizontal='justify'
        alignmentVertical='center'
        spacing='small'
        width='100%'
        spacingInset='none 500 none none'
      >
        <Text type='b200' color='secondary'>
          <FormattedMessage id='cs_perfportal_tm' defaultMessage='TM' />
        </Text>
        <Text type='b200' color='secondary'>
          <FormattedMessage
            id='cs_perfportal_invalid_flags_outliers'
            defaultMessage='Invalid flags'
          />
        </Text>
      </Row>

      {tmStats.map((tmOutlierInfo, index) => (
        <Row
          key={index}
          alignmentHorizontal='start'
          alignmentVertical='center'
          spacing='small'
          spacingInset='200 none'
          width='100%'
        >
          <UserBadgeThumbnails usernames={[tmOutlierInfo.tmAlias]} />
          <Column spacingInset='100 100 100 450' spacing='200' width={175}>
            <Text type='b300'>{tmOutlierInfo.tmAlias}</Text>
            <Text type='b300' color='secondary'>
              <FormattedMessage
                id='cs_perfportal_outlier_associate_num'
                defaultMessage='{numAssociates} Associates'
                values={{ numAssociates: tmOutlierInfo.numAssociates }}
              />
            </Text>
          </Column>
          <Row spacing='100' alignmentVertical='bottom' spacingInset='None 500'>
            <Text type='h500'>{tmOutlierInfo.numInvalidFlags}</Text>
          </Row>
        </Row>
      ))}
    </Column>
  );

  return expandedContent;
};
export default ExceptionOverviewCard;
